import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { UserWithRole } from 'better-auth/plugins/admin';

import { useSession } from '@/hooks/authHooks';
import { authClient } from '@/lib/authClient';
import { isAdminRole } from '@/utils/auth';

export const DEFAULT_ADMIN_USERS_PAGE_SIZE = 10;
export const ADMIN_USERS_QUERY_KEY = ['admin-users'] as const;

export interface AdminUsersResponse {
  users: UserWithRole[];
  total: number;
  limit?: number;
  offset?: number;
}

export interface UseAdminUsersParams {
  page?: number;
  pageSize?: number;
  searchValue?: string;
}

export function useAdminUsers({
  page = 1,
  pageSize = DEFAULT_ADMIN_USERS_PAGE_SIZE,
  searchValue,
}: UseAdminUsersParams = {}) {
  const { user } = useSession();
  const offset = (page - 1) * pageSize;

  return useQuery<AdminUsersResponse>({
    queryKey: [...ADMIN_USERS_QUERY_KEY, page, pageSize, searchValue],
    queryFn: async () => {
      const response = await authClient.admin.listUsers({
        query: {
          limit: pageSize,
          offset,
          searchValue: searchValue?.trim() || undefined,
          searchField: searchValue ? 'email' : undefined,
          // We default to contains to support partial email search.
          searchOperator: searchValue ? 'contains' : undefined,
        },
        fetchOptions: { throw: true },
      });

      return response;
    },
    placeholderData: (previousData) => previousData,
    enabled: isAdminRole(user?.role),
    staleTime: 30_000,
  });
}

type SetUserRoleVariables = {
  userId: string;
  role: string;
};

type SetUserRoleOptions = Omit<
  UseMutationOptions<UserWithRole, Error, SetUserRoleVariables>,
  'mutationFn'
>;

export function useSetUserRoleMutation(options?: SetUserRoleOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: SetUserRoleVariables) => {
      const response = await authClient.admin.setRole({
        userId,
        // @ts-expect-error the role type is not correctly inferred
        role: role,
        fetchOptions: { throw: true },
      });

      return response.user;
    },
    ...options,
    onSuccess: (updatedUser, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      options?.onSuccess?.(updatedUser, variables, onMutateResult, context);
    },
  });
}

type RemoveUserResponse = {
  success: boolean;
};

type RemoveUserVariables = {
  userId: string;
};

type RemoveUserOptions = Omit<
  UseMutationOptions<RemoveUserResponse, Error, RemoveUserVariables>,
  'mutationFn'
>;

export function useRemoveUserMutation(options?: RemoveUserOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: RemoveUserVariables) => {
      return authClient.admin.removeUser({
        userId,
        fetchOptions: { throw: true },
      });
    },
    ...options,
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      options?.onSuccess?.(response, variables, onMutateResult, context);
    },
  });
}
